<script setup>
  import { computed } from 'vue';
  import { useDateHelper } from "@/composables/useDateHelper";
  import useConfigurationGlobals from '@/composables/useConfigurationGlobals';
  import useSettingsGlobals from '@/composables/useSettingsGlobals';
  import { format } from 'date-fns';
  import Icon from './icons/Icon.vue';

  const { $BRAND } = useConfigurationGlobals();
  const { $WEB_VIEW } = useSettingsGlobals();

  const formattedBrandNames = {
    ikitesurf: 'iKitesurf',
    sailflow: 'SailFlow',
    fishweather: 'FishWeather',
    windalert: 'WindAlert',
    iwindsurf: 'iWindsurf',
  }

  const props = defineProps({
    proForecast: {
      type: Object,
      required: true
    },
    forecaster: {
      type: Object,
      default: null
    },
    headerLevel: {
      type: [Number, String],
      default: 1
    },
    extended: {
      type: Boolean,
      default: false
    },
    requestedForecastType: {
      type: String,
      default: 'daily'
    }
  });

  const dateHelper = useDateHelper();

  const isRequestedForecastType = computed(() => (props.requestedForecastType === 'daily' && !props.extended) || (props.requestedForecastType === 'extended' && props.extended));

  const localTime = computed(() => {
    const useIssuedTimestamp = props.proForecast.is_sample_forecast || !Object.prototype.hasOwnProperty.call(props.proForecast, 'timestamp_published_local');
    const localTimestamp = useIssuedTimestamp ? props.proForecast.issued_timestamp_local : props.proForecast.timestamp_published_local;
    const localTimestampISOFormatted = dateHelper.datetimeToISOFormat(localTimestamp);
    const date = new Date(localTimestampISOFormatted);

    return `${format(date, "eee, LLL d")} at ${format(date, "p")}`;
  });

  const nextUpdateTime = computed(() => {
    const nextUpdateTime = props.proForecast.next_update_time_local;
    let updateTimeLocal;
    let formattedDate;

    try {
      updateTimeLocal = dateHelper.datetimeToISOFormat(nextUpdateTime);
      
      formattedDate = format(updateTimeLocal, "eee, LLL d 'by' h:mm a");
    } catch (error) {
      console.error('Invalid next update time:', props.proForecast.next_update_time_local);
      return 'Next update time not available';
    }

    return `Next update: ${formattedDate}`;
  });

  const formattedBrandName = computed(() => formattedBrandNames[$BRAND.toLowerCase()]);

  const forecastName = computed(() => {
    return props.extended 
      ? 'Professional Meteorologist Extended Forecast' 
      : 'Professional Meteorologist Forecast';

    if ($WEB_VIEW) {
      return `${authorTypeTextString}`
    }

    return `${props.proForecast.forecast_name} ${authorTypeTextString}`;
  });

  const showByLineForecasterPhoto = computed(() => !props.extended || isRequestedForecastType.value);
  const forecasterBioUrl = computed(() => props.forecaster ? `https://tempest.earth/forecasters/${props.forecaster.bio_slug}/` : null);

</script>

<template>
  <header class="jwx-pro-forecast__header jwx-page__section">
    <!-- <p class="jwx-pro-forecast__masthead" v-if="isRequestedForecastType && !$WEB_VIEW">{{ formattedBrandName }}</p> -->
    <p class="jwx-pro-forecast__forecast-name" v-html="forecastName"></p>
    <component :is="`h${props.headerLevel}`" class="jwx-pro-forecast__headline">{{ props.proForecast.headline }}</component>
    <div class="jwx-pro-forecast__forecaster-date-block" :data-has-photo="showByLineForecasterPhoto">
      <div v-if="showByLineForecasterPhoto" class="jwx-pro-forecast__forecaster-image">
        <img v-if="props.forecaster" :src="`https://cdn-consumer.weatherflow.com/pro-forecasts/forecasters/photos/${props.forecaster.photo_filename}`" :alt="`${props.proForecast.forecaster} image`" class="jwx-pro-forecast__forecaster-photo" />
        <Icon v-else name="user" />
      </div>
      <p v-if="forecasterBioUrl" class="jwx-pro-forecast__by-line">by <a :href="forecasterBioUrl" target="_blank" class="jwx-pro-forecast__forecaster">{{ props.proForecast.forecaster }}</a></p>
      <p v-else-if="showByLineForecasterName" class="jwx-pro-forecast__by-line">by <span class="jwx-pro-forecast__forecaster">{{ props.proForecast.forecaster }}</span></p>
      <p class="jwx-pro-forecast__publish-date">Published {{ localTime }}</p>
    </div>
    <div class="jwx-pro-forecast__next-update">
      <p class="jwx-pro-forecast__update-time">{{ nextUpdateTime }}</p>
      <p class="jwx-pro-forecast__update-notice">Special updates issued as needed</p>
    </div>
  </header>
</template>